import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Building2, Home, Palmtree } from "lucide-react";
import { cn } from "@/lib/utils";

interface TeamAvailabilityData {
  inOffice: number;
  wfh: number;
  onLeave: number;
  total: number;
  onLeaveNames: string[];
  wfhNames: string[];
}

interface TeamAvailabilityWidgetProps {
  data?: TeamAvailabilityData;
}

export default function TeamAvailabilityWidget({ data }: TeamAvailabilityWidgetProps) {
  if (!data) {
    return (
      <Card className="h-full border-0 shadow-lg bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 shadow-md">
              <Users className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Team Today</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="text-center py-6 text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Loading team data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sections = [
    {
      label: 'In Office',
      count: data.inOffice,
      icon: Building2,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
      names: [] as string[],
    },
    {
      label: 'WFH',
      count: data.wfh,
      icon: Home,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
      names: data.wfhNames || [],
    },
    {
      label: 'On Leave',
      count: data.onLeave,
      icon: Palmtree,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950/30',
      names: data.onLeaveNames || [],
    },
  ];

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 shadow-md">
              <Users className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Team Today</CardTitle>
          </div>
          <span className="text-sm text-muted-foreground font-medium">{data.total} members</span>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        <div className="grid grid-cols-3 gap-3">
          {sections.map(({ label, count, icon: Icon, color, bgColor, names }) => (
            <div key={label} className={cn("rounded-xl p-3 text-center", bgColor)}>
              <div className={cn("flex items-center justify-center gap-1.5 mb-1", color)}>
                <Icon className="h-4 w-4" />
                <span className="text-2xl font-bold">{count}</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">{label}</span>
              {names.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {names.slice(0, 3).map((name, i) => (
                    <p key={i} className="text-xs text-muted-foreground truncate">{name}</p>
                  ))}
                  {names.length > 3 && (
                    <p className="text-xs text-muted-foreground">+{names.length - 3} more</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
